import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import useMedia from 'hooks/useMedia';
import type { WithTestId } from 'types/app';

import { QualifiersEnum } from '@Modules/qualifiers/constants';
import type { OfferType } from '@Modules/offers/types';
import { selectQualifier } from '@Modules/qualifiers/selectors';

import styles from './offerStatsToast.module.scss';

const DEFAULT_DURATION_MS = 5000;

type OfferStatsToastProps = {
  toast: OfferType['toast'];
  className?: string;
};

export const OfferStatsToast: FC<WithTestId<OfferStatsToastProps>> = ({
  toast,
  className,
  testId = 'offer_stats_toast',
}) => {
  const isEnabled = Boolean(useSelector(selectQualifier(QualifiersEnum.offerToast)));
  const body = toast?.body?.trim();
  const { xs, sm } = useMedia();
  const [visible, setVisible] = useState(true);

  const isMobile = xs || sm;
  const durationByViewport = isMobile ? DEFAULT_DURATION_MS : DEFAULT_DURATION_MS * 2;
  const durationRef = useRef(durationByViewport);
  durationRef.current = durationByViewport;

  useEffect(() => {
    if (!isEnabled || !body) {
      return undefined;
    }

    setVisible(true);
    const timer = setTimeout(() => setVisible(false), durationRef.current);

    return () => clearTimeout(timer);
    // re-arm only when navigating to a different offer's toast — not on resize
  }, [isEnabled, body, toast?.id]);

  if (!isEnabled || !body || !visible) {
    return null;
  }

  return (
    <div
      className={cn(styles.toast, className)}
      role="status"
      aria-live="polite"
      data-testid={testId}
    >
      <span className={styles.text}>{body}</span>
    </div>
  );
};
